import * as React from "react";
import { useMappedState } from "redux-react-hook";
import * as moment from "moment-timezone";
import _ from "lodash";

import {
  getDeliveryCutoff,
  getDeliveryRoute,
  getDeliveryWeekLeadTimes,
  getMinimumOrderQuantitySetting,
  getMinimumOrderValue,
  getOrderMode
} from "src/core/drops/utils/inherited-settings";
import {
  deliveryDateHasPassed,
  orderMinimumQuantityMet,
  orderMinimumValueNotMet,
  productMinimumQuantityNotMet,
  validateAgainstMaxPackSizeConstraints
} from "src/core/orders/utils/orders";
import { useDrop } from "src/core/drops/hooks";
import type { IStore } from "src/core/shared/store";
import {
  IOrder,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import { ErrorLevel } from "src/core/products/api/products.api";
import { useDropShutdowns } from "src/modules/shutdowns/hooks/useDropShutdowns";

import useOrderingNotAvailableForDay from "./useOrderingAvailableForDay";
import useOrderIsBeforeCutoff from "./useOrderIsBeforeCutoff";
import useValidAgainstProductRelatedGroups from "./useValidAgainstProductRelatedGroups";
import { useOrderVariant } from "../useOrderVariant";

/**
 * Check if the order passes validation
 * @return {string | null} - The reason it didn't pass, or null if it does pass.
 */
const useCanSubmitOrder = () => {
  const orderIsBeforeCutOff = useOrderIsBeforeCutoff();
  const [ { drop } ] = useDrop();
  const orderingNotAvailableForDay = useOrderingNotAvailableForDay(drop);
  const validAgainstProductRelatedGroups = useValidAgainstProductRelatedGroups();
  const { checkShutdownDate } = useDropShutdowns();
  const getOrderVariant = useOrderVariant();

  const {
    productRelatedGroups,
    installedModules,
    terminology,
    packSizeConstraints,
    orderVariants
  } = useMappedState(({ global }: IStore) => ({
    shutdowns: global.settings?.shutdowns || null,
    productRelatedGroups: global.settings?.productRelatedGroups || null,
    installedModules: global?.settings?.installedModules || [],
    terminology: global?.settings?.terminology,
    packSizeConstraints: global?.settings?.packSizeConstraints,
    orderVariants: global?.settings?.orderVariants || []
  }));

  const canSubmitOrder = React.useCallback(
    (order: IOrder): string | null => {
      const dropToUse = drop || order.branch;

      if (!dropToUse) {
        return "Drop not found, please exit the order before continuing.";
      }
      const deliveryRoute = getDeliveryRoute(dropToUse.groupedSettings);
      const leadTimes = getDeliveryWeekLeadTimes(dropToUse.groupedSettings);
      const minimumOrderValue = getMinimumOrderValue(dropToUse.groupedSettings);
      const minimumOrderQuantitySetting = getMinimumOrderQuantitySetting(dropToUse.groupedSettings);
      const orderMode = getOrderMode(dropToUse.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(dropToUse.groupedSettings);

      if (!dropToUse.groupedSettings || !minimumOrderValue || !leadTimes || !deliveryRoute || !minimumOrderQuantitySetting || !orderMode || !defaultCutoff) {
        return "Order cannot be submitted";
      }

      // Only do delivery date validation if it's a date-led order
      if (order.orderDeliveryDate) {
        // if the date matches a shutdown dont allow creation
        const hasShutdownDate = checkShutdownDate(order.orderDeliveryDate, order.orderVariantId);

        if (hasShutdownDate) {
          return `Order amendments on this day have been blocked due to - ${hasShutdownDate.shutdown.reason}`;
        }

        // if the delivery date is in the past then - FAIL
        if (deliveryDateHasPassed(order.orderDeliveryDate)) {
          return "Order cannot be submitted, as delivery date is in the past";
        }

        // if the deliverydate is before cutoff then - FAIL
        if (orderIsBeforeCutOff(order, "longest")) {
          if (order.amendmentExpiryDate && moment.utc().isAfter(order.amendmentExpiryDate) && moment.utc(order.amendmentExpiryDate).isAfter(moment.utc())) {
            return "Order cannot be amended, as the amendment expiry time has passed";
          }

          return "Order cannot be submitted, as cutoff has passed";
        }

        // if the delivery date is in the past then - FAIL
        const orderingNotAvailableOnDay = orderingNotAvailableForDay(order.orderDeliveryDate, getOrderVariant(order.orderVariantId));

        if (orderingNotAvailableOnDay) {
          return orderingNotAvailableOnDay;
        }
      }

      // if the delivery date is in the past then - FAIL
      if (orderMinimumValueNotMet(order, minimumOrderValue)) {
        return `Order cannot be submitted, as the minimum order value of £${minimumOrderValue.value} was not met`;
      }

      // if no order lines
      if (order.orderLines.length === 0) {
        return "Order must contain at least one product to be submitted.";
      }

      const minOrderQtyError = orderMinimumQuantityMet(order, minimumOrderQuantitySetting, terminology);

      // if the order doesn't meet min quantity setting then - FAIL
      if (minOrderQtyError) {
        return minOrderQtyError;
      }

      // validate order against pack size constraints
      const orderConstraintViolation = validateAgainstMaxPackSizeConstraints(order, orderVariants, packSizeConstraints, terminology);

      // if constraint violated - FAIL
      if (orderConstraintViolation) {
        return orderConstraintViolation;
      }

      if (order.paymentStatus === OrderPaymentStatus.CAPTURED || order.paymentStatus === OrderPaymentStatus.UNCAPTURED) {
        return "An order cannot be changed after payment has been taken";
      }

      if (order.paymentStatus === OrderPaymentStatus.FAILED) {
        return "Payment failed, the order cannot be changed while the payment is failed";
      }

      // if an order includes a product below minimum order quantity - FAIL
      const productMinimumQuantityFailed = productMinimumQuantityNotMet(order.orderLines);

      if (productMinimumQuantityFailed) {
        return productMinimumQuantityFailed;
      }

      // check related group logic
      if (installedModules.includes("product-related-group") && productRelatedGroups) {
        const { message, errorLevel } = validAgainstProductRelatedGroups(order.orderLines);

        if (errorLevel === ErrorLevel.Error) {
          // Error message should come from the related group settings.
          // The default is incase this hasn't been set in the DB against the product_related_group.setting.
          return message || "Invalid mix of products. Please speak to an administrator.";
        }
      }

      return null;
    },
    [
      drop,
      terminology,
      orderVariants,
      packSizeConstraints,
      installedModules,
      productRelatedGroups,
      checkShutdownDate,
      orderIsBeforeCutOff,
      orderingNotAvailableForDay,
      getOrderVariant,
      validAgainstProductRelatedGroups
    ]
  );

  return canSubmitOrder;
};

export default useCanSubmitOrder;
